import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const isFormData = data instanceof FormData;
  
  // Build headers
  const headers: Record<string, string> = {};
  if (data && !isFormData) {
    headers["Content-Type"] = "application/json";
  }
  
  // Extract document ID from URL and get current user
  const docMatch = url.match(/\/api\/documents\/([^\/]+)/);
  if (docMatch) {
    const documentId = docMatch[1];
    const storageKey = `document-user-${documentId}`;
    const storedUser = localStorage.getItem(storageKey);
    
    if (storedUser) {
      const user = JSON.parse(storedUser);
      headers['x-document-user-id'] = user.id;
    }
  }
  
  const res = await fetch(url, {
    method,
    headers,
    body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    const headers: Record<string, string> = {};
    // Attach guest document-user identity for document-scoped queries
    const docMatch = url.match(/\/api\/documents\/([^/]+)/);
    if (docMatch) {
      try {
        const storedUser = localStorage.getItem(`document-user-${docMatch[1]}`);
        if (storedUser) {
          headers["x-document-user-id"] = JSON.parse(storedUser).id;
        }
      } catch {}
    }
    const res = await fetch(url, {
      credentials: "include",
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retryOnMount: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
